import { DOMType, mergeAttributes, } from '@tencent/exeditor3-core';
import '@tencent/exeditor3-ui-kit/dist/toolbar-button.js';
import { pickFile } from '@tencent/exeditor3-ui-kit/dist/utils.js';
import { createIcon } from '@tencent/exeditor3-ui-kit/dist/icon.js';
import { FilePlugin, fileStyles, uploadingContainerClassName, } from '@tencent/exeditor3-template-plugin-file';
import { getImageMetaInfo, base64StringToTypedArray } from './utils.js';
import { transformAttrsToDOMRules, parseImageAttrsFromNode, } from './transforms.js';
import { PictureEditView } from './picture-edit-view.js';
import { InlinePictureEditView } from './inline-picture-edit-view.js';
import './picture-block-container.js';
import picturePluginStyles from './styles.js';
const styles = `
${fileStyles}
${picturePluginStyles}
`;
const defaultOption = {
    upload: () => {
        throw new Error('upload() method has not been set.');
    },
    accept: 'image/*',
    acceptFile: (file) => /^image\//i.test(file.type),
    allowFloat: false,
    editable: true,
    resizeOnRotate: true,
    hasTitle: true,
};
const containerClassName = 'exeditor-picture';
export class PicturePlugin extends FilePlugin {
    constructor(options = {}) {
        super();
        this.name = 'picture';
        this.nameCN = '图片';
        this.spec = {
            type: DOMType.BLOCK,
            matchSelector: `.${uploadingContainerClassName}[data-plugin="picture"], .${containerClassName}, img`,
            attrs: {
                info: {
                    parse: parseImageAttrsFromNode,
                },
                needToUpload: {
                    parse: (node) => node.dataset.needToUpload || '',
                },
                uploading: {
                    parse: (node) => node.dataset.uploading || '',
                },
                fail: {
                    parse: (node) => node.dataset.fail || '',
                    toDOMAttribute: (fail) => (fail ? { fail } : {}),
                },
            },
            toDOM: (node, mergedAttributes) => {
                var _a;
                const { uploading } = node.attrs;
                const { src, width, height } = node.attrs.info || {};
                const title = this.options.hasTitle ? (_a = node.attrs.info) === null || _a === void 0 ? void 0 : _a.title : '';
                if (uploading) {
                    return this.wrapUploadingDOMRule(node.attrs, {
                        tag: 'img',
                        attributes: mergeAttributes([
                            mergedAttributes,
                            { src, title, width, height },
                        ]),
                    });
                }
                return transformAttrsToDOMRules(node.attrs.info, this.options, mergedAttributes);
            },
            draggable: true,
            renderAttrsToEditView: true,
        };
        this.editView = (node, getPos) => {
            if (node.attrs.uploading) {
                return this.editPlaceholderView(node, getPos);
            }
            if (this.isNeedToUploadBySrc(node.attrs.info)) {
                this.uploadBySrc(node.attrs.info, getPos);
                return this.editPlaceholderView(node, getPos);
            }
            if (node.attrs.needToUpload) {
                this.uploadSourceFile(node, getPos);
            }
            if (this.options.inline) {
                return new InlinePictureEditView(this.editor, node, getPos, this.options);
            }
            return new PictureEditView(this.editor, node, getPos, this.options);
        };
        this.options = {
            ...defaultOption,
            ...options,
        };
        this.spec.type = this.options.inline ? DOMType.INLINE_BLOCK : DOMType.BLOCK;
    }
    initEditor(editor) {
        this.editor = editor;
    }
    capabilities() {
        const capabilities = super.capabilities();
        return {
            ...capabilities,
            styles,
        };
    }
    renderToolbarItem() {
        const btn = document.createElement('exeditor-toolbar-button');
        Object.assign(btn, {
            label: this.editor.t('图片'),
            onclick: () => {
                pickFile(this.options.accept, true).then((files) => {
                    if (!(files === null || files === void 0 ? void 0 : files.length)) {
                        return;
                    }
                    this.editor.focus();
                    this.insertByFiles(Array.from(files));
                });
            },
        }).appendChild(createIcon({
            name: this.name,
            editor: this.editor,
            fallback: () => import('./icon.js').then((m) => m.pictureIcon),
        }));
        return btn;
    }
    createContentElement(info) {
        const image = document.createElement('img');
        image.src = info.src;
        image.style.maxWidth = '100%';
        if (info.width) {
            image.width = info.width;
        }
        if (info.height) {
            image.height = info.height;
        }
        return image;
    }
    calculateInitialScale(width) {
        if (!this.options.inline) {
            // 非行内模式使用百分比，不限制最大宽度
            return 1;
        }
        // 判断是否超出编辑区
        const editorEl = this.editor.getRootDOM().firstElementChild;
        const editorStyles = window.getComputedStyle(editorEl);
        const containerWidth = editorEl.clientWidth -
            parseFloat(editorStyles.paddingLeft) -
            parseFloat(editorStyles.paddingRight);
        if (containerWidth < width) {
            return containerWidth / width;
        }
        return 1;
    }
    async getMetadata(result, info) {
        const metaInfo = await getImageMetaInfo(typeof result === 'string' ? { src: result } : result);
        if (info.width && info.height) {
            metaInfo.width = info.width;
            metaInfo.height = info.height;
        }
        else if (metaInfo.width) {
            // 如果有默认的尺寸，不再重算尺寸
            const scale = this.calculateInitialScale(metaInfo.width);
            metaInfo.width = metaInfo.width * scale;
            if (metaInfo.height) {
                metaInfo.height = metaInfo.height * scale;
            }
        }
        return metaInfo;
    }
    uploadSourceFile(node, getPos) {
        var _a, _b;
        const attrs = node.attrs;
        const title = ((_a = attrs.info) === null || _a === void 0 ? void 0 : _a.title) || '';
        const srcString = ((_b = attrs.info) === null || _b === void 0 ? void 0 : _b.src) || '';
        const dataPieces = srcString.split(/[:;,]/);
        const [dataTag, type, dataType, base64Data] = dataPieces;
        if (dataTag !== 'data' || !type || dataType !== 'base64') {
            return;
        }
        const uploading = `file-uploading-${this.name}-${this.getUploadId()}`;
        const file = new File([base64StringToTypedArray(base64Data)], title, {
            type,
        });
        const newAttrs = {
            ...attrs,
            uploading,
            needToUpload: '',
        };
        setTimeout(() => {
            const pos = getPos();
            this.editor.noHistory().patchNodeAttr(pos, newAttrs);
            const uploadFn = () => this.options.upload(file);
            this.upload(uploadFn, uploading, newAttrs.info);
        });
    }
}
//# sourceMappingURL=index.js.map